import { useState } from "react";
import type { AccountDto, SendMailRequest } from "../../../shared/types";
import { useSendMail } from "../../hooks/useApi";

function parseAddresses(value: string): string[] {
  return value
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function ComposeDialog({
  accountId,
  accounts,
  initial,
  quotedHtml,
  replyTo,
  title = "New mail",
  onClose,
}: {
  accountId: number;
  /** In the combined "All" view: real accounts to pick the sender from. */
  accounts?: AccountDto[];
  /** Prefill for reply/forward. */
  initial?: { to?: string; cc?: string; subject?: string };
  /** Original message HTML appended below the typed reply on send. */
  quotedHtml?: string;
  /** Threading context — providers set In-Reply-To/References/thread ids. */
  replyTo?: SendMailRequest["replyTo"];
  title?: string;
  onClose: () => void;
}) {
  const [to, setTo] = useState(initial?.to ?? "");
  const [cc, setCc] = useState(initial?.cc ?? "");
  const [subject, setSubject] = useState(initial?.subject ?? "");
  const [body, setBody] = useState("");
  const [sent, setSent] = useState(false);
  const [fromId, setFromId] = useState(() =>
    accountId !== 0 ? accountId : (accounts?.[0]?.id ?? 0),
  );
  const sendMail = useSendMail(fromId);

  const send = () => {
    sendMail.mutate(
      {
        to: parseAddresses(to),
        cc: parseAddresses(cc),
        subject,
        // Plain text typed in the textarea, sent as minimal HTML. The quoted
        // original travels separately so each provider can thread it natively.
        body: body
          .split("\n")
          .map(
            (line) => `<p>${line.replace(/&/g, "&amp;").replace(/</g, "&lt;") || "&nbsp;"}</p>`,
          )
          .join(""),
        quotedHtml,
        replyTo,
      },
      {
        onSuccess: () => {
          setSent(true);
          setTimeout(onClose, 1200);
        },
      },
    );
  };

  const inputClass =
    "w-full rounded border border-stroke px-2.5 py-1.5 text-[13px] outline-none focus:border-accent";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/30" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="flex w-[640px] max-w-[92vw] flex-col gap-2.5 rounded-lg border border-stroke bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-[15px] font-semibold">{title}</h2>
        {accounts && accounts.length > 0 && (
          <select
            className={inputClass}
            value={fromId}
            aria-label="Send from account"
            onChange={(e) => setFromId(Number(e.target.value))}
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                From: {a.username}
              </option>
            ))}
          </select>
        )}
        <input
          className={inputClass}
          placeholder="To (separate with ; or ,)"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          autoFocus={!initial?.to}
        />
        <input
          className={inputClass}
          placeholder="Cc"
          value={cc}
          onChange={(e) => setCc(e.target.value)}
        />
        <input
          className={inputClass}
          placeholder="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <textarea
          className={`${inputClass} h-52 resize-y`}
          placeholder="Write your message…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          autoFocus={!!initial?.to}
        />
        {quotedHtml && (
          <p className="text-[11px] text-ink-muted">
            The original message is included below your reply.
          </p>
        )}
        {sendMail.error && (
          <p className="text-[12px] text-red-700">Send failed: {sendMail.error.message}</p>
        )}
        <div className="flex items-center gap-2">
          <button
            onClick={send}
            disabled={sendMail.isPending || sent || fromId === 0 || parseAddresses(to).length === 0}
            className="rounded bg-accent px-4 py-1.5 text-[13px] font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {sent ? "Sent ✓" : sendMail.isPending ? "Sending…" : "Send"}
          </button>
          <button
            onClick={onClose}
            className="rounded border border-stroke px-4 py-1.5 text-[13px] hover:bg-black/5"
          >
            Discard
          </button>
        </div>
      </div>
    </div>
  );
}
